import {
  Alert,
  AlertDescription,
  AlertIcon,
  Center,
  HStack,
  Heading,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Skeleton,
} from '@chakra-ui/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  ValleyBankSubLoanAccount,
  getSubLoansTotalOutstandingBalance,
  isContributesToOutstandingBalance,
  ValleyBankLoanAccount,
} from '@finley-technologies/valley-bank-utils';

import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from '~/packages/common-frontend/components/icons';
import { ColumnDisplaySettingsButton } from '~/packages/common-frontend/components/TanStack/ColumnReordering';
import {
  createDefaultTableColumnConfiguration,
  getColumnNames,
  TableColumnConfigurationKey,
  useTableColumnPreferences,
} from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';
import { LoanAccountSummaryLink } from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';

type PageType = 'customers' | 'loan-accounts' | 'arrangement-groups';

type LoanAccountLoanPartyMap = {
  [loanAccountNumber: string]: {
    [cifNumber: string]: ValleyBankLoanAccount['loanParties'][number];
  };
};

type CustomerTypeRequiredInputs = {
  cifNumber: string;
  loanAccountLoanPartyMap: LoanAccountLoanPartyMap;
};

type MaybeProperties<T extends object> = {
  [K in keyof T]?: T[K] | null;
};

const TABLE_KEY_CUSTOMERS: TableColumnConfigurationKey =
  'valleyBankSbl.customerLoans';

const TABLE_KEY_LOAN_ACCOUNTS: TableColumnConfigurationKey =
  'valleyBankSbl.loanAccountLoanSummary';

const TABLE_KEY_ARRANGEMENT_GROUPS: TableColumnConfigurationKey =
  'valleyBankSbl.arrangementGroupLoanAccounts';

const DEFAULT_COLUMNS_CONFIG_CUSTOMERS = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY_CUSTOMERS,
  columns: [
    {
      columnKey: 'contributesToOutstandingBalance',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'productDescription',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'customerType',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'commitmentId',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'noteId',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'loanAmount',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'currentBalance',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'noteDate',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'maturityDate',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'interestRate',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'currency',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'fxAmount',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'fxRate',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'source',
      isVisible: false,
      isFixed: false,
    },
    {
      columnKey: 'usdCurrentNotionalAmount',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'initialMarginRequirementRate',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'initialMarginRequirementAmount',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'markToMarket',
      isVisible: true,
      isFixed: false,
    },
  ],
});

const DEFAULT_COLUMNS_CONFIG_LOAN_ACCOUNTS = createDefaultTableColumnConfiguration(
  {
    tableKey: TABLE_KEY_LOAN_ACCOUNTS,
    columns: [
      {
        columnKey: 'contributesToOutstandingBalance',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'productDescription',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'commitmentId',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'noteId',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'loanAmount',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'currentBalance',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'noteDate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'maturityDate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'interestRate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'currency',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'fxAmount',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'fxRate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'source',
        isVisible: false,
        isFixed: false,
      },
      {
        columnKey: 'usdCurrentNotionalAmount',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'initialMarginRequirementRate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'initialMarginRequirementAmount',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'markToMarket',
        isVisible: true,
        isFixed: false,
      },
    ],
  },
);

const DEFAULT_COLUMNS_CONFIG_ARRANGEMENT_GROUPS = createDefaultTableColumnConfiguration(
  {
    tableKey: TABLE_KEY_ARRANGEMENT_GROUPS,
    columns: [
      {
        columnKey: 'contributesToOutstandingBalance',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'productDescription',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'commitmentId',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'noteId',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'loanAmount',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'currentBalance',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'noteDate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'maturityDate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'interestRate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'currency',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'fxAmount',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'fxRate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'source',
        isVisible: false,
        isFixed: false,
      },
      {
        columnKey: 'usdCurrentNotionalAmount',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'initialMarginRequirementRate',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'initialMarginRequirementAmount',
        isVisible: true,
        isFixed: false,
      },
      {
        columnKey: 'markToMarket',
        isVisible: true,
        isFixed: false,
      },
    ],
  },
);

const getDefaultConfigInputs = (pageType: PageType) => {
  switch (pageType) {
    case 'customers':
      return {
        tableKey: TABLE_KEY_CUSTOMERS,
        defaultTableColumnConfiguration: DEFAULT_COLUMNS_CONFIG_CUSTOMERS,
      };
    case 'loan-accounts':
      return {
        tableKey: TABLE_KEY_LOAN_ACCOUNTS,
        defaultTableColumnConfiguration: DEFAULT_COLUMNS_CONFIG_LOAN_ACCOUNTS,
      };
    case 'arrangement-groups':
      return {
        tableKey: TABLE_KEY_ARRANGEMENT_GROUPS,
        defaultTableColumnConfiguration: DEFAULT_COLUMNS_CONFIG_ARRANGEMENT_GROUPS,
      };
    default:
      return finleyAssertUnreachable(pageType);
  }
};

const getIdWithoutLoanAccoutNumber = ({
  loanAccountNumber,
  id,
}: {
  loanAccountNumber: string;
  id: string | null;
}): string | null => {
  if (!id) return id;
  const regex = new RegExp(`^${loanAccountNumber}(.*)`, 'g');
  const capturingGroup = regex.exec(id)?.[1];
  return capturingGroup ?? id;
};

type CommitmentOrNoteIdWithLoanAccountLinkProps = {
  loanAccountNumber: string;
  id: string;
};
const CommitmentOrNoteIdWithLoanAccountLink = ({
  loanAccountNumber,
  id,
}: CommitmentOrNoteIdWithLoanAccountLinkProps) => {
  const idWithoutLoanAccountNumber = getIdWithoutLoanAccoutNumber({
    loanAccountNumber,
    id,
  });
  return (
    <>
      <LoanAccountSummaryLink loanAccountId={loanAccountNumber}>
        {loanAccountNumber}
      </LoanAccountSummaryLink>
      {idWithoutLoanAccountNumber !== ''
        ? `-${idWithoutLoanAccountNumber}`
        : null}
    </>
  );
};

const BASE_COLUMN_DEFS: ColumnDef<ValleyBankSubLoanAccount, any>[] = [
  {
    id: 'contributesToOutstandingBalance',
    header: 'Contributes to Outstanding Balance',
    accessorFn: (row) => isContributesToOutstandingBalance(row),
    cell: function formatContributesToOutstandingBalance({ getValue }) {
      return getValue() ? (
        <Center>
          <Icon icon="check" />
        </Center>
      ) : null;
    },
  },
  {
    id: 'productDescription',
    header: 'Product Name',
    accessorKey: 'productDescription',
    cell: function formatProductDescription({ getValue }) {
      return getValue() ?? '-';
    },
  },
  {
    id: 'commitmentId',
    header: 'Commitment ID',
    accessorKey: 'commitmentId',
    cell: function formatCommitmentId({
      row: {
        original: { accountNumber, commitmentId },
      },
    }) {
      return commitmentId ? (
        <CommitmentOrNoteIdWithLoanAccountLink
          loanAccountNumber={accountNumber}
          id={commitmentId}
        />
      ) : (
        '-'
      );
    },
  },
  {
    id: 'noteId',
    header: 'Note ID',
    accessorKey: 'noteId',
    cell: function formatNoteId({
      row: {
        original: { accountNumber, noteId },
      },
    }) {
      return noteId ? (
        <CommitmentOrNoteIdWithLoanAccountLink
          loanAccountNumber={accountNumber}
          id={noteId}
        />
      ) : (
        '-'
      );
    },
  },
  {
    id: 'loanAmount',
    header: 'Loan Amount',
    accessorKey: 'loanAmount',
    cell: function formatLoanAmount({ getValue }) {
      return formatCurrencyAmount(getValue() ?? 0, Bank.Currency.USD);
    },
  },
  {
    id: 'currentBalance',
    header: 'Current Balance',
    accessorKey: 'currentBalance',
    cell: function formatCurrentBalance({ getValue }) {
      return getValue()
        ? formatCurrencyAmount(getValue(), Bank.Currency.USD)
        : '-';
    },
  },
  {
    id: 'noteDate',
    header: 'Note Date',
    accessorKey: 'noteDate',
  },
  {
    id: 'maturityDate',
    header: 'Maturity Date',
    accessorKey: 'maturityDate',
  },
  {
    id: 'interestRate',
    header: 'Interest Rate',
    accessorKey: 'interestRate',
    cell: function formatInterestRate({ getValue }) {
      return getValue()
        ? percentageToPercentageLabel(getValue() * 100, 2)
        : '-';
    },
  },
  {
    id: 'currency',
    header: 'Currency',
    accessorKey: 'currency',
    cell: function formatCurrency({ getValue }) {
      return getValue() ?? '-';
    },
  },
  {
    id: 'fxAmount',
    header: 'FX Amount',
    accessorKey: 'fxAmount',
    cell: function formatFxAmount({ getValue }) {
      return getValue() ?? '-';
    },
  },
  {
    id: 'fxRate',
    header: 'FX Rate',
    accessorKey: 'fxRate',
    cell: function formatFxRate({ getValue }) {
      return getValue() ?? '-';
    },
  },
  {
    id: 'source',
    header: 'Source',
    accessorKey: 'source',
  },
  {
    id: 'usdCurrentNotionalAmount',
    header: 'USD Current Notional Amount',
    accessorKey: 'usdCurrentNotionalAmount',
    cell: function formatFxAmount({ getValue }) {
      return typeof getValue() === 'number'
        ? formatCurrencyAmount(getValue(), Bank.Currency.USD)
        : '-';
    },
  },
  {
    id: 'initialMarginRequirementRate',
    header: 'Initial Margin Requirement Rate',
    accessorKey: 'initialMarginRequirementRate',
    cell: function formatInterestRate({ getValue }) {
      return typeof getValue() === 'number'
        ? percentageToPercentageLabel(getValue() * 100, 2)
        : '-';
    },
  },
  {
    id: 'initialMarginRequirementAmount',
    header: 'Initial Margin Requirement Amount',
    accessorKey: 'initialMarginRequirementAmount',
    cell: function formatFxAmount({ getValue }) {
      return typeof getValue() === 'number'
        ? formatCurrencyAmount(getValue(), Bank.Currency.USD)
        : '-';
    },
  },
  {
    id: 'markToMarket',
    header: 'Mark To Market',
    accessorKey: 'markToMarket',
    cell: function formatFxAmount({ getValue }) {
      return typeof getValue() === 'number'
        ? formatCurrencyAmount(getValue(), Bank.Currency.USD)
        : '-';
    },
  },
];

const generateCustomerTypeColumnDef = ({
  cifNumber,
  loanAccountLoanPartyMap,
}: CustomerTypeRequiredInputs): ColumnDef<ValleyBankSubLoanAccount, any> => ({
  id: 'customerType',
  header: 'Customer Type',
  accessorFn: (row) => {
    return loanAccountLoanPartyMap && cifNumber
      ? loanAccountLoanPartyMap[row.accountNumber][cifNumber]
          .rel_code_description
      : undefined;
  },
  cell: function formatCustomerType({ getValue }) {
    return getValue() ?? '-';
  },
});

const COLUMN_NAMES = {
  ...getColumnNames(BASE_COLUMN_DEFS),
  customerType: 'Customer Type',
};

const useColumnDefs = ({
  cifNumber,
  loanAccountLoanPartyMap,
}: MaybeProperties<CustomerTypeRequiredInputs>) => {
  const columnDefs: ColumnDef<ValleyBankSubLoanAccount, any>[] = useMemo(() => {
    return cifNumber && loanAccountLoanPartyMap
      ? [
          ...BASE_COLUMN_DEFS,
          generateCustomerTypeColumnDef({ cifNumber, loanAccountLoanPartyMap }),
        ]
      : BASE_COLUMN_DEFS;
  }, [cifNumber, loanAccountLoanPartyMap]);

  return {
    columnDefs,
  };
};

type LoansTableProps = {
  loans: ValleyBankSubLoanAccount[];
  noLoansMessage: string;
  title?: string;
  showStats?: boolean;
  cifNumber?: string | null;
  loanAccountLoanPartyMap?: LoanAccountLoanPartyMap;
  isLoading: boolean;
  isError: boolean;
  downloadFilenamePrefix: string;
  pageType: PageType;
};
export const LoansTable = ({
  loans,
  noLoansMessage,
  title,
  showStats = true,
  cifNumber,
  loanAccountLoanPartyMap,
  isLoading,
  isError,
  downloadFilenamePrefix,
  pageType,
}: LoansTableProps) => {
  const { tableKey, defaultTableColumnConfiguration } = useMemo(() => {
    return getDefaultConfigInputs(pageType);
  }, [pageType]);

  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({ tableKey, defaultTableColumnConfiguration });

  const { columnDefs } = useColumnDefs({
    cifNumber,
    loanAccountLoanPartyMap,
  });

  const table = useReactTable({
    data: loans,
    columns: columnDefs,
    state: {
      columnOrder,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  return (
    <VStack w="100%" spacing="24px" alignItems="flex-start">
      {title && showStats && (
        <Heading as="h2" fontSize="24px" mb="24px">
          {title}
        </Heading>
      )}
      {loans.length === 0 && (
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>{noLoansMessage}</AlertDescription>
        </Alert>
      )}
      {loans.length > 0 && (
        <>
          {showStats && (
            <HStack spacing="24px" w="100%">
              <InfoCard
                title="Total Outstanding Balance"
                value={formatCurrencyAmount(
                  getSubLoansTotalOutstandingBalance(loans),
                  Bank.Currency.USD,
                )}
                icon={<Icon icon="dollar" size="24px" />}
              />
            </HStack>
          )}
          {/* @jendevelops TODO implement search and show/hide columns */}
          <HStack
            w="100%"
            spacing="16px"
            justify={title && !showStats ? 'space-between' : 'flex-end'}
            alignItems="center"
          >
            {/* <Button variant="outline" colorScheme="aurora">
          Show/Hide Columns
        </Button> */}
            {title && !showStats && (
              <Heading as="h2" fontSize="24px" mb="24px">
                {title}
              </Heading>
            )}
            <HStack spacing="1em">
              <ColumnDisplaySettingsButton
                initialColumns={columnConfiguration}
                updateColumnConfigs={updateUserColumnConfiguration}
                columnNames={COLUMN_NAMES}
              />
              <CsvXlsxDownloadSelect
                dataRows={csvDownload.dataRows}
                headers={csvDownload.headers}
                filenamePrefix={downloadFilenamePrefix}
              />
            </HStack>
          </HStack>
          <TableContainer
            mt="32px"
            // maxH required for table top sticky header to work
            // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
            // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
            overflowX="auto"
            overflowY="auto"
            position="relative"
            id="receivables-dashboard-main-table"
            w="100%"
          >
            <Table id="valley-bank-loans-table">
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <Th key={header.id}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              {isLoading && !isError && (
                <Tbody>
                  {Array.from(Array(10).keys()).map((i) => (
                    <Tr key={i}>
                      {table.getVisibleFlatColumns().map((column) => (
                        <Td
                          key={`${column.id}+${i}`}
                          padding="8px 16px"
                          height="56px"
                        >
                          <Skeleton
                            h="100%"
                            w={
                              column.columnDef.header === 'Borrower'
                                ? '400px'
                                : undefined
                            }
                          />
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              )}
              {!isLoading && !isError && (
                <Tbody>
                  {table.getRowModel().rows.map((row) => (
                    <Tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Td
                          key={cell.id}
                          textAlign={
                            cell.column.columnDef.meta?.style?.textAlign
                          }
                          padding={cell.column.columnDef.meta?.style?.padding}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              )}
            </Table>
          </TableContainer>
        </>
      )}
    </VStack>
  );
};
